import {MenuConfig, MenuItemConfig} from '../core/settings/settings';
import {useAuth} from '../auth/use-auth';
import {useSettings} from '../core/settings/use-settings';
import {isDashboardAiChatMenuItem} from './dashboard-ai-chat-menu';
import dot from 'dot-object';
import {useMemo} from 'react';

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();

  const {user, hasPermission} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    if (menu) {
      const chatUrl = settings.librechat_public_url;
      menu.items = menu.items.map(item => {
        // Bare #hash only works on the homepage; off-site pages need /#hash.
        // Added: 2026-10-08.
        let action = item.action;
        if (typeof action === 'string' && action.startsWith('#')) {
          action = `/${action}`;
        }

        if (isDashboardAiChatMenuItem(item) && chatUrl) {
          return {
            ...item,
            type: 'link' as const,
            action: chatUrl,
            target: '_blank' as const,
          };
        }

        if (action !== item.action) {
          return {...item, action};
        }
        return item;
      });
      menu.items = menu.items.filter(item => {
        const hasRoles = (item.roles || []).every(
          a => user?.roles.find(b => b.id === a),
        );
        const hasPermissions = (item.permissions || []).every(a =>
          hasPermission(a),
        );
        const hasSettings =
          !item.settings ||
          Object.entries(item.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        // make sure item has action, otherwise router link will error out
        // Content Studio menu: STUDIO_ENABLED. Added: 2026-09-11. Changed: 2026-09-22.
        const studioOk =
          item.action !== '/dashboard/studio' ||
          settings.studio_enabled === true;
        // Dashboard LibreChat: LIBRECHAT_ENABLED. Added: 2026-10-09.
        const librechatOk =
          !isDashboardAiChatMenuItem(item) ||
          settings.librechat_enabled === true;

        return (
          item.action &&
          hasRoles &&
          hasPermissions &&
          hasSettings &&
          studioOk &&
          librechatOk
        );
      });
    }

    return menu;
  }, [hasPermission, settings, menuOrPosition, user]);
}
